import moment from "moment";
import "moment/locale/es";

export const isResolvedPresenceRequest = (request) =>
  ["approved", "rejected"].includes(request.status);
export const presenceRequestKey = (request) =>
  String(request.id ?? request["@id"]);
export const presenceRecordType = (type) =>
  ({
    entry: "Entrada",
    pause: "Parada",
    resume: "Reanudación",
    exit: "Salida",
  })[type] ||
  type ||
  "Fichaje";
export const presenceRecordTime = (value) =>
  value && moment(value).isValid() ? moment(value).format("HH:mm") : "—";
export const presenceChangeDay = (value) => {
  const date = moment(value, "DD-MM-YYYY", true).locale("es");
  if (!date.isValid()) return value || "Fecha no disponible";
  return date
    .format("DD [de] MMMM [de] YYYY")
    .replace(
      /(\sde\s)(\p{L})/u,
      (_, prefix, letter) => prefix + letter.toUpperCase(),
    );
};

const dayTimestamp = (day) => {
  const date = moment(
    day,
    ["DD-MM-YYYY", "D-MM-YYYY", "DD-M-YYYY", "D-M-YYYY"],
    true,
  );
  return date.isValid() ? date.valueOf() : -Infinity;
};

export const sortPresenceChangeDays = (days) =>
  [...days].sort((first, second) => {
    const firstDate = dayTimestamp(first.day);
    const secondDate = dayTimestamp(second.day);
    return firstDate === secondDate ? 0 : secondDate - firstDate;
  });

const relationKey = (record) =>
  typeof record === "object" && record !== null
    ? (record["@id"] ?? record.id)
    : record;
const sameRecord = (original, relation) => {
  const key = relationKey(relation);
  return (
    key != null &&
    [original["@id"], original.id].some(
      (value) => value != null && String(value) === String(key),
    )
  );
};
const byTime = (a, b) => (Date.parse(a.time) || 0) - (Date.parse(b.time) || 0);
const changeKind = (request) =>
  ["cancellation", "deletion", "cancel"].includes(request.requestType)
    ? "cancelled"
    : request.requestType === "new"
      ? "new"
      : "modification";
const toRequestRow = (request, index, original) => ({
  key: `request-${presenceRequestKey(request)}-${index}`,
  type: request.type || original?.type,
  time:
    request.requestedRecordedAt || request.recordedAt || original?.recordedAt,
  kind: changeKind(request),
  request,
});

// Keep every request, including several changes linked to the same original record.
export const buildPresenceChangeRows = (requests = {}) => {
  const originals = requests.originalWorkRecords || [];
  const changes = requests.workRecordRequests || [];
  const originalRows = originals
    .map((record, index) => ({
      key: `original-${record["@id"] ?? record.id ?? index}`,
      type: record.type,
      time: record.recordedAt,
      kind: "unchanged",
    }))
    .sort(byTime);
  const requestedRows = originals
    .filter(
      (record) =>
        !changes.some((request) =>
          sameRecord(record, request.originalWorkRecord),
        ),
    )
    .map((record, index) => ({
      key: `unchanged-${index}`,
      type: record.type,
      time: record.recordedAt,
      kind: "unchanged",
    }));
  const historyRows = changes.map((request, index) =>
    toRequestRow(
      request,
      index,
      originals.find((record) =>
        sameRecord(record, request.originalWorkRecord),
      ) ||
        (typeof request.originalWorkRecord === "object"
          ? request.originalWorkRecord
          : null),
    ),
  );
  return {
    originalRows,
    requestedRows: [...requestedRows, ...historyRows].sort(byTime),
    historyRows: [...historyRows].sort(byTime),
  };
};
